import { TEventInputData } from '@/core/constants/event_types/additional_types';
import { TMableProduct } from '@/core/constants/event_types/extended_mable_event';
import { Logger } from '@/shared/logger/logger';

import {
  IShopifyCartItem,
  IShopifyCartState,
  ShopifyCartHandler,
} from '../handlers/shopify_cart_handler';
import {
  CpxCartLine,
  CustomPixelStdEventName,
  ShpyCpxHandlerInput,
} from '../shopify_custom_pixel_types/cpx_payload_types';

export const PRODUCT_ADDED_GENERATED_TAG = 'product_added_generated';

/**
 * Service class for processing cart data from various Shopify Custom Pixel events
 */
export class CartProcessingService {
  // Events that need `cartLine` processed to update cart data instead of `cart` object.
  static CPX_CART_LINE_EVENTS = [
    CustomPixelStdEventName.PRODUCT_ADDED_TO_CART,
    CustomPixelStdEventName.PRODUCT_REMOVED_FROM_CART,
  ];

  /**
   * Processes any cpx event, updates the cart and return additional add to cart events if needed
   *
   * Rules:
   * - If current cart doesn't match the cart in the event, trigger add to cart and update cart
   * - If current cart matches the cart in the event, do nothing
   *
   * @param cpx_input_payload The Shopify Custom Pixel event payload
   * @returns Array of ATC Event Input Data triggered, empty array if there is no change
   */
  public static calcAddToCartEventsFromCpxInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): TEventInputData[] {
    try {
      // Get the current cart handler
      const cartHandler = ShopifyCartHandler.getInstance();

      // Get the latest cart state
      const newCartState =
        CartProcessingService.processCartDataFromCpxPayload(cpx_input_payload);

      // No new cart state
      if (!newCartState) return [];

      // Compare with previous cart state to detect changes
      const cartChanges = cartHandler.detectCartChanges(newCartState);
      const { addedItems, changed, currentCount, previousCount } = cartChanges;

      // If carts are equal, do nothing
      if (!changed) {
        Logger.debug('No cart changes detected on event', {
          currentCount,
          previousCount,
        });
        return [];
      }

      Logger.info('Cart changes detected on event', {
        addedItems,
        currentCount,
        previousCount,
      });

      // Update the cart state in storage
      cartHandler.updateCartStateFromData(newCartState);

      // If the current event is an ATC event, skip
      if (
        cpx_input_payload.event.name ===
        CustomPixelStdEventName.PRODUCT_ADDED_TO_CART
      ) {
        Logger.info('Skip cart changes detected on atc event');
        return [];
      }

      const atcEvents: TEventInputData[] = [];

      // If items were added, create an ATC event

      for (const item of addedItems) {
        // Convert IShopifyCartItem to TMableProduct

        const currencyCode =
          item.currency ||
          cpx_input_payload.init?.data?.shop?.paymentSettings?.currencyCode ||
          'EUR';

        const productForEvent: TMableProduct = {
          br: item.vendor || '',
          cat: item.product_type,
          cur: currencyCode,
          id: item.product_id,
          ids: {
            pid: item.product_id || '',
            sku: item.sku || '',
            vid: item.variant_id || '',
          },
          nam: item.title || '',
          prc: item.price?.toString() || '0', // Default as we don't have price in IShopifyCartItem
          qty: item.quantity,
          totalDiscount: 0,
          var: item.variant_title || '',
        };

        // Create ATC event input with required properties
        const atcEventInput: TEventInputData = {
          cdas: [], // Add empty cdas array to satisfy the type requirement
          eventData: {
            act_product: productForEvent,
            cv_data: {
              cv: item.price || 0,
              discount: 0,
              org_cv: item.price || 0,
              shipping: 0,
              tax: 0,
            },
            qty_act_product: item.quantity,
            tags: [PRODUCT_ADDED_GENERATED_TAG],
          },
        };

        atcEvents.push(atcEventInput);
      }

      Logger.debug(atcEvents.length + ' ATC event(s) triggered');

      return atcEvents;
    } catch (error) {
      Logger.error('Error processing cart data from event', error);
      return [];
    }
  }

  /**
   * Processes an non-Add To Cart event and updates the cart state
   *
   * @param cpx_input_payload The Shopify Custom Pixel event payload
   * @returns the latest cart state or null if there was no cart data
   */
  public static processCartDataFromCpxPayload(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): IShopifyCartState | null {
    // Check if cart data should be processed from cartLine
    if (
      CartProcessingService.CPX_CART_LINE_EVENTS.includes(
        cpx_input_payload.event.name,
      )
    )
      return CartProcessingService.processCartDataFromCartLine(
        cpx_input_payload,
      );

    // Check if cart property is present in init.data
    if (!cpx_input_payload.init?.data?.cart) {
      Logger.debug('No cart data found in cpx event');
      return null;
    }

    // Get cart timestamp from event / fallback to client time
    let cartUpdateTimestamp: number;
    try {
      cartUpdateTimestamp = new Date(
        cpx_input_payload.event.timestamp,
      ).getTime();
    } catch {
      cartUpdateTimestamp = Date.now();
    }

    // Convert CpxCart to IShopifyCartState format
    const cpxCart = cpx_input_payload.init.data.cart;
    const newCartState: IShopifyCartState = {
      item_count: cpxCart.totalQuantity,
      items: cpxCart.lines.map(line => {
        const cpxProduct = line.merchandise.product;
        const priceInfo = line.merchandise.price;
        return {
          currency: priceInfo.currencyCode,
          id: cpxProduct.id,
          price: priceInfo.amount,
          product_id: cpxProduct.id,
          product_type: cpxProduct.type,
          quantity: line.quantity,
          sku: cpxProduct.sku,
          title: cpxProduct.title,
          variant_id: line.merchandise.id,
          variant_title: line.merchandise.title,
          vendor: cpxProduct.vendor,
        };
      }),
      last_updated: cartUpdateTimestamp,
      total_price: cpxCart.cost?.totalAmount?.amount || 0,
    };

    return newCartState;
  }

  /**
   * Processes an product added / removed event and updates the cart state
   *
   * @param cpx_input_payload The Shopify Custom Pixel event payload
   * @returns the latest cart state or null if it could not be extracted
   */
  public static processCartDataFromCartLine(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): IShopifyCartState | null {
    try {
      // Get the current cart handler
      const cartHandler = ShopifyCartHandler.getInstance();

      // If cart data is not in the payload, construct it from the event data
      const cartLine = cpx_input_payload.event.data?.cartLine as CpxCartLine;
      const currentCartState = cartHandler.getCurrentCartState();
      const isProductRemovedEvent =
        cpx_input_payload.event.name ==
        CustomPixelStdEventName.PRODUCT_REMOVED_FROM_CART;

      if (currentCartState && cartLine?.merchandise) {
        // Get merchandise ID with null check - ensure it's never undefined
        const merchandiseId = cartLine.merchandise.id?.toString() || '';
        const cpxProduct = cartLine.merchandise.product;

        // Skip processing if we don't have a valid merchandise ID
        if (!merchandiseId) {
          return null;
        }

        // Item Key = productId_variantId
        const currItemKey = `${cpxProduct.id}_${merchandiseId}`;

        // Find if the item already exists in the cart
        const existingItemIndex = currentCartState.items.findIndex(
          item => cartHandler.getCartItemKey(item) === currItemKey,
        );

        const newItems = Array.from(currentCartState.items);
        const quantity = cartLine.quantity || 1;
        const quantityChange = isProductRemovedEvent ? -quantity : quantity;

        Logger.debug('Processing cartLine update', {
          itemKey: currItemKey,
          quantityChange,
        });

        // Update item if it exists or add new item
        if (existingItemIndex >= 0 && newItems[existingItemIndex]) {
          const existingItem = newItems[existingItemIndex];
          // Make sure existingItem is defined and create a type-safe update
          newItems[existingItemIndex] = {
            ...existingItem,
            quantity: Math.max(0, existingItem.quantity + quantityChange),
          };
        } else {
          // Add new item with all required fields
          const newCartItem: IShopifyCartItem = {
            currency: cartLine.merchandise.price.currencyCode,
            id: cpxProduct.id,
            price: cartLine.merchandise.price?.amount || 0,
            product_id: cpxProduct?.id?.toString() || '',
            product_type: cpxProduct.type,
            quantity,
            sku: cpxProduct.sku,
            title: cartLine.merchandise.product?.title || '',
            variant_id: merchandiseId,
            variant_title: cartLine.merchandise?.title || '',
            vendor: cpxProduct.vendor,
          };

          // Ensure we have a non-undefined id
          if (newCartItem.id) {
            newItems.push(newCartItem);
          }
        }

        // Calculate new total quantity
        const newTotalQuantity = newItems.reduce(
          (sum, item) => sum + item.quantity,
          0,
        );

        // Filter out any invalid items
        const validItems = newItems.filter(item => !!item.id);

        // Get cart timestamp from event / fallback to client time
        let cartUpdateTimestamp: number;
        try {
          cartUpdateTimestamp = new Date(
            cpx_input_payload.event.timestamp,
          ).getTime();
        } catch {
          cartUpdateTimestamp = Date.now();
        }

        // Update cart state
        const newCartState: IShopifyCartState = {
          item_count: newTotalQuantity,
          items: validItems,
          last_updated: cartUpdateTimestamp,
          total_price:
            currentCartState.total_price + cartLine.cost.totalAmount.amount ||
            0, // We don't have accurate price info without fetching
        };

        // Log cart update for debugging
        Logger.debug(
          'Updated cart state from event data in add-to-cart event',
          {
            items_added:
              newCartState.items.length - currentCartState.items.length,
            new_count: newCartState.item_count,
            previous_count: currentCartState.item_count,
          },
        );

        return newCartState;
      }

      return null;
    } catch (error) {
      Logger.error('Error processing cart data from add to cart event', error);
      return null;
    }
  }
}
